<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';

const { t } = useI18n();

const buildDefaultPayload = () => ({
  summary: {
    totalRequests: 0,
    completedPauses: 0,
    activePauses: 0,
    pendingRequests: 0,
    exceededPauses: 0,
    totalPauseMinutes: 0,
    averagePauseMinutes: 0,
  },
  agents: [],
  recentPauses: [],
});

const isLoading = ref(false);
const report = ref(buildDefaultPayload());

const formatMinutes = minutes => `${minutes || 0} min`;

const summaryCards = computed(() => [
  {
    key: 'totalRequests',
    label: t('PAUSE_REPORTS.SUMMARY.TOTAL_REQUESTS'),
    value: report.value.summary.totalRequests,
    icon: 'i-lucide-clipboard-list',
    tone: 'text-n-slate-12 bg-n-alpha-2',
  },
  {
    key: 'activePauses',
    label: t('PAUSE_REPORTS.SUMMARY.ACTIVE'),
    value: report.value.summary.activePauses,
    icon: 'i-lucide-timer',
    tone: report.value.summary.activePauses
      ? 'text-n-teal-11 bg-n-teal-5/70'
      : 'text-n-slate-12 bg-n-alpha-2',
  },
  {
    key: 'pendingRequests',
    label: t('PAUSE_REPORTS.SUMMARY.PENDING'),
    value: report.value.summary.pendingRequests,
    icon: 'i-lucide-bell-ring',
    tone: report.value.summary.pendingRequests
      ? 'text-n-amber-11 bg-n-amber-5/70'
      : 'text-n-slate-12 bg-n-alpha-2',
  },
  {
    key: 'exceededPauses',
    label: t('PAUSE_REPORTS.SUMMARY.EXCEEDED'),
    value: report.value.summary.exceededPauses,
    icon: 'i-lucide-octagon-alert',
    tone: report.value.summary.exceededPauses
      ? 'text-n-ruby-11 bg-n-ruby-5/70'
      : 'text-n-slate-12 bg-n-alpha-2',
  },
  {
    key: 'totalPauseMinutes',
    label: t('PAUSE_REPORTS.SUMMARY.TOTAL_TIME'),
    value: formatMinutes(report.value.summary.totalPauseMinutes),
    icon: 'i-lucide-clock-3',
    tone: 'text-n-slate-12 bg-n-alpha-2',
  },
  {
    key: 'averagePauseMinutes',
    label: t('PAUSE_REPORTS.SUMMARY.AVERAGE_TIME'),
    value: formatMinutes(report.value.summary.averagePauseMinutes),
    icon: 'i-lucide-chart-column-big',
    tone: 'text-n-slate-12 bg-n-alpha-2',
  },
]);

const fetchPauseReport = async ({ from, to }) => {
  isLoading.value = true;

  try {
    const response = await ReportsAPI.getPauseReports({ from, to });
    report.value = {
      ...buildDefaultPayload(),
      ...camelcaseKeys(response.data, { deep: true }),
    };
  } catch (error) {
    useAlert(t('PAUSE_REPORTS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const formatDateTime = isoString => {
  if (!isoString) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(isoString));
};

const pauseStatusLabel = pause => {
  if (pause.overdue) return t('PAUSE_REPORTS.STATUS.OVERDUE');
  if (pause.status === 'active') return t('PAUSE_REPORTS.STATUS.ACTIVE');
  if (pause.status === 'completed') return t('PAUSE_REPORTS.STATUS.COMPLETED');
  if (pause.status === 'rejected') return t('PAUSE_REPORTS.STATUS.REJECTED');
  if (pause.status === 'canceled') return t('PAUSE_REPORTS.STATUS.CANCELED');

  return pause.status;
};

const pauseStatusClass = pause => {
  if (pause.overdue) return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  if (pause.status === 'active')
    return 'bg-n-teal-5 text-n-teal-11 outline-n-teal-8/60';
  if (pause.status === 'completed')
    return 'bg-n-slate-4 text-n-slate-12 outline-n-strong/50';
  if (pause.status === 'rejected' || pause.status === 'canceled') {
    return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  }

  return 'bg-n-amber-5 text-n-amber-11 outline-n-amber-8/60';
};

const showTimingBadge = pause => pause.timingStatus !== 'not_applicable';

const pauseTimingLabel = pause => {
  if (pause.timingStatus === 'exceeded') {
    return t('PAUSE_REPORTS.TIMING.EXCEEDED');
  }

  return t('PAUSE_REPORTS.TIMING.WITHIN_TIME');
};

const pauseTimingClass = pause => {
  if (pause.timingStatus === 'exceeded') {
    return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  }

  return 'bg-n-teal-5 text-n-teal-11 outline-n-teal-8/60';
};

const recentTimestampLabel = pause => {
  if (pause.completedAt) return t('PAUSE_REPORTS.RECENT.ENDED_AT');
  if (pause.canceledAt) return t('PAUSE_REPORTS.RECENT.CANCELED_AT');
  if (pause.rejectedAt) return t('PAUSE_REPORTS.RECENT.REJECTED_AT');

  return t('PAUSE_REPORTS.RECENT.PLANNED_END');
};

const recentTimestampValue = pause =>
  pause.completedAt || pause.canceledAt || pause.rejectedAt || pause.endsAt;
</script>

<template>
  <ReportHeader
    :header-title="$t('PAUSE_REPORTS.HEADER')"
    :header-description="$t('PAUSE_REPORTS.DESCRIPTION')"
  />

  <ReportFilters
    :show-group-by="false"
    :show-business-hours="false"
    :show-entity-filter="false"
    @filter-change="fetchPauseReport"
  />

  <div class="flex flex-col gap-6 pt-5 pb-8">
    <div class="grid gap-4 lg:grid-cols-3">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="rounded-2xl bg-n-surface-2 p-5 outline outline-1 outline-n-weak"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="text-sm text-n-slate-11">
              {{ card.label }}
            </div>
            <div class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ card.value }}
            </div>
          </div>
          <div
            class="grid size-11 place-items-center rounded-2xl"
            :class="card.tone"
          >
            <Icon :icon="card.icon" class="size-5" />
          </div>
        </div>
      </div>
    </div>

    <section
      class="rounded-[28px] bg-n-surface-2 p-6 outline outline-1 outline-n-weak"
    >
      <div class="flex flex-col gap-1">
        <div class="text-lg font-semibold text-n-slate-12">
          {{ $t('PAUSE_REPORTS.AGENTS.TITLE') }}
        </div>
        <div class="text-sm text-n-slate-11">
          {{ $t('PAUSE_REPORTS.AGENTS.DESCRIPTION') }}
        </div>
      </div>

      <div
        v-if="!report.agents.length && !isLoading"
        class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
      >
        {{ $t('PAUSE_REPORTS.AGENTS.EMPTY') }}
      </div>

      <div v-else class="mt-5 grid gap-3">
        <article
          v-for="agent in report.agents"
          :key="agent.id"
          class="rounded-2xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
        >
          <div
            class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
          >
            <div class="flex items-start gap-4 min-w-0">
              <Avatar :name="agent.name" :size="40" />
              <div class="min-w-0">
                <div class="text-base font-medium text-n-slate-12">
                  {{ agent.name }}
                </div>
                <div class="mt-1 text-sm text-n-slate-11">
                  {{ agent.email }}
                </div>
              </div>
            </div>

            <div
              class="grid gap-3 sm:grid-cols-3 xl:grid-cols-6 xl:min-w-[46rem]"
            >
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.PAUSE_COUNT') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ agent.pauseCount }}
                </div>
              </div>
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.ACTIVE_COUNT') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ agent.activeCount }}
                </div>
              </div>
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.EXCEEDED_COUNT') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ agent.exceededCount }}
                </div>
              </div>
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.WITHIN_TIME_COUNT') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ agent.withinTimeCount }}
                </div>
              </div>
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.TOTAL_TIME') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ formatMinutes(agent.totalPauseMinutes) }}
                </div>
              </div>
              <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                <div class="text-xs uppercase tracking-wide text-n-slate-10">
                  {{ $t('PAUSE_REPORTS.AGENTS.AVERAGE_TIME') }}
                </div>
                <div class="mt-1 text-sm text-n-slate-12">
                  {{ formatMinutes(agent.averagePauseMinutes) }}
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section
      class="rounded-[28px] bg-n-surface-2 p-6 outline outline-1 outline-n-weak"
    >
      <div class="flex flex-col gap-1">
        <div class="text-lg font-semibold text-n-slate-12">
          {{ $t('PAUSE_REPORTS.RECENT.TITLE') }}
        </div>
        <div class="text-sm text-n-slate-11">
          {{ $t('PAUSE_REPORTS.RECENT.DESCRIPTION') }}
        </div>
      </div>

      <div
        v-if="!report.recentPauses.length && !isLoading"
        class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
      >
        {{ $t('PAUSE_REPORTS.RECENT.EMPTY') }}
      </div>

      <div
        v-else-if="isLoading"
        class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
      >
        {{ $t('PAUSE_REPORTS.LOADING') }}
      </div>

      <div v-else class="mt-5 grid gap-3">
        <article
          v-for="pause in report.recentPauses"
          :key="pause.id"
          class="rounded-2xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="text-base font-medium text-n-slate-12">
                {{ pause.agent.name }}
              </div>
              <div class="mt-1 text-sm text-n-slate-11">
                {{ pause.label }}
              </div>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <span
                v-if="showTimingBadge(pause)"
                class="rounded-full px-2.5 py-1 text-xs font-medium outline outline-1"
                :class="pauseTimingClass(pause)"
              >
                {{ pauseTimingLabel(pause) }}
              </span>
              <span
                class="rounded-full px-2.5 py-1 text-xs font-medium outline outline-1"
                :class="pauseStatusClass(pause)"
              >
                {{ pauseStatusLabel(pause) }}
              </span>
            </div>
          </div>

          <div class="mt-4 grid gap-3 sm:grid-cols-4">
            <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
              <div class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ $t('PAUSE_REPORTS.RECENT.REQUESTED_AT') }}
              </div>
              <div class="mt-1 text-sm text-n-slate-12">
                {{ formatDateTime(pause.requestedAt) }}
              </div>
            </div>
            <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
              <div class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ $t('PAUSE_REPORTS.RECENT.PLANNED_DURATION') }}
              </div>
              <div class="mt-1 text-sm text-n-slate-12">
                {{ formatMinutes(pause.durationInMinutes) }}
              </div>
            </div>
            <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
              <div class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ $t('PAUSE_REPORTS.RECENT.ACTUAL_DURATION') }}
              </div>
              <div class="mt-1 text-sm text-n-slate-12">
                {{ formatMinutes(pause.actualDurationInMinutes) }}
              </div>
            </div>
            <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
              <div class="text-xs uppercase tracking-wide text-n-slate-10">
                {{ recentTimestampLabel(pause) }}
              </div>
              <div class="mt-1 text-sm text-n-slate-12">
                {{ formatDateTime(recentTimestampValue(pause)) }}
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>
